import { useEffect, useState, useRef, useContext } from "react"
import PlayLargeIcon from "../../icons/PlayLargeIcon"
import DownloadIcon from "../../icons/DownloadIcon"
import TrashIcon from "../../icons/TrashIcon"
import JoinIcon from "../../icons/JoinIcon"
import { GoPencil } from "react-icons/go"
import { MixtapePageContext } from "./MixtapePageContainer"
import cx from "classnames"
import LeaveIcon from "../../icons/LeaveIcon"
import CollaborateOnIcon from "../../icons/CollaborateOnIcon"
import CollaborateOffIcon from "../../icons/CollaborateOffIcon"
import LongRunningProcess from "../../shared/utils/LongRunningProcess"
import MixtapeDownloadModal from "./MixtapeDownloadModal"
import { gql, useMutation } from "@apollo/client"
import { editMixtape, deleteMixtape } from "../../shared/utils/modalHelper"
import { openLogin } from "../../../../../javascript/services/modal"

const PLAY_MIXTAPE = gql`
  mutation PlayMixtape($id: ID!) {
    playMixtape(id: $id) {
      songVersions {
        id
        title
        customizable
        displayName
        exclusive
        hasClickLicenses
        length
        lyrics
        playerArt
        songVersionFile
        artist {
          id
          name
        }
      }
    }
  }
`

const MixtapeActions = () => {
  const { user_role, is_owner, mixtapeSerializer, mixtapeDetails, setMixtapeDetails } = useContext(MixtapePageContext)
  const [playMixtape, { loading }] = useMutation(PLAY_MIXTAPE)
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState("")
  const downloadButtonRef = useRef(null)

  const handleDownloadAll = () => {
    if (isDownloading) return
    setIsDownloading(true)
  }

  const handleOpenEditModal = () => {
    editMixtape(mixtapeSerializer.id)
  }

  const handleDeleteMixtape = async () => {
    deleteMixtape(mixtapeSerializer.id)
  }

  const handlePlayAll = () => {
    const variables = { id: mixtapeSerializer.id }
    playMixtape({ variables })
      .then(({ data: { _ } }) => {
        if (
          Array.isArray(mixtapeSerializer.song_versions) &&
          mixtapeSerializer.song_versions.length > 0
        ) {
          const playlistEvent = new CustomEvent("load-playlist", {
            detail: { items: mixtapeSerializer.song_versions },
          })
          document.dispatchEvent(playlistEvent)
          
          const songVersionDetail = {
            detail: mixtapeSerializer.song_versions[0],
          }
          const playEvent = new CustomEvent("start-playlist", songVersionDetail)
          document.dispatchEvent(playEvent)
        } else {
          console.warn("No songs available to play in this mixtape.")
        }
      })
      .catch((err) => console.log(err))
  }

  const toggleJoinUnjoin = async () => {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const result = await fetch(
      `/mixtapes/${mixtapeSerializer.id}/${
        mixtapeDetails.includes_current_user ? "leave" : "join"
      }`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
        },
      }
    )
    if (!result.ok) {
      return
    }
    const response = await result.json()
    if (response.success) {
      setMixtapeDetails({
        ...mixtapeDetails,
        includes_current_user: !mixtapeDetails.includes_current_user,
      })
    }
  }

  const toggleMakeCollaborative = async () => {
    const result = await fetch(`/mixtapes/${mixtapeSerializer.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": mm.csrf,
      },
      body: JSON.stringify({
        mixtape: {
          collaborative: mixtapeDetails.all_collaborative ? "no" : "all",
        },
      }),
    })
    if (!result.ok) {
      return
    }
    setMixtapeDetails({ ...mixtapeDetails, all_collaborative: !mixtapeDetails.all_collaborative })
  }

  const handleDownloadSuccess = (data) => {
    if (downloadUrl !== "") return
    if (data.success) {
      setDownloadUrl(data.url)
    } else {
      setIsDownloading(false)
    }
  }

  const handleCloseModal = (e) => {
    setDownloadUrl("")
    setIsDownloading(false)
  }

  useEffect(() => {
    if (downloadUrl) {
      downloadButtonRef.current?.click()
    }
  }, [downloadUrl])

  return (
    <div className="actions">
      <div className="contain">
        {downloadUrl !== "" && (
          <MixtapeDownloadModal
            downloadUrl={downloadUrl}
            onClose={handleCloseModal}
            downloadRef={downloadButtonRef}
          />
        )}
        <ul className={cx(is_owner && "owner")}>
          <li
            className="action play mm-hover-connect"
            onClick={() => handlePlayAll()}
          >
            <span className="icon">
              <PlayLargeIcon />
            </span>
            <span className="label">Play All</span>
          </li>

          {/** Display the download button only if there are more than 0 songs */}
          <li
            className={cx(
              "action download mm-hover-connect",
              user_role == "guest" && "login-required",
              isDownloading && "disabled"
            )}
            data-action={user_role !== "guest" && "download"}
            data-connect="mixtape-actions"
            data-cy={cx("download", user_role === "guest" && "disabled")}
          >
            <a
              id="downloadLink"
              onClick={user_role !== "guest" ? handleDownloadAll : openLogin}
              data-prevent-default={user_role === "guest"}
              itemProp="contentUrl"
            >
              <span data-cy="download-temp-song" className="icon">
                <DownloadIcon />
              </span>
              <span className="label">Download Playlist</span>
            </a>
          </li>

          {is_owner && mixtapeDetails.collaborative != "marmo" && (
            <li
              className={cx(
                "action collaborate",
                mixtapeDetails.all_collaborative ? "collaborative" : "uncollaborative"
              )}
              onClick={toggleMakeCollaborative}
            >
              <span className="icon add-collab">
                <CollaborateOnIcon />
              </span>
              <span className="icon remove-collab">
                <CollaborateOffIcon />
              </span>
              <span className="label">
                {mixtapeDetails.all_collaborative ? "Make Uncollaborative" : "Make Collaborative"}
              </span>
            </li>
          )}
          {!is_owner &&
            ((user_role !== "guest" && mixtapeDetails.all_collaborative) ||
              mixtapeDetails.marmo_only_and_internal) && (
              <li
                className={cx(
                  "action join",
                  mixtapeDetails.includes_current_user ? "joined" : "unjoined",
                  "mm-hover-connect"
                )}
                onClick={toggleJoinUnjoin}
              >
                <span className="icon join-playlist">
                  <JoinIcon />
                </span>
                <span className="icon leave-playlist">
                  <LeaveIcon />
                </span>
                <span className="label">
                  { mixtapeDetails.includes_current_user ? "Leave Playlist" : "Join Playlist"}
                </span>
              </li>
            )}
            {is_owner && (
              <li
                className="action edit mm-hover-connect"
                data-connect="mixtape-actions"
                onClick={handleOpenEditModal}
              >
                <span className="icon">
                  <GoPencil viewBox="0 0 30 25" />
                </span>
                <span className="label">Edit</span>
              </li>
            )}
            {is_owner && (
              <li
                className="action delete mm-hover-connect"
                data-connect="mixtape-actions"
                onClick={handleDeleteMixtape}
              >
                <span className="icon">
                  <TrashIcon />
                </span>
                <span className="label">Delete</span>
              </li>
            )}
        </ul>
        <div className="spin-targ contain">
          {isDownloading && (
            <LongRunningProcess
              url={`/mixtapes/${mixtapeSerializer.id}/download`}
              pollInterval={2000}
              timeout={300000}
              callback={handleDownloadSuccess}
            ></LongRunningProcess>
          )}
        </div>
      </div>
    </div>
  )
}

export default MixtapeActions
